import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '#shared/providers/Api';
import { useAlertDialog } from '#shared/providers/AlertDialog';

type UseLabelsOptions = {
  // Omit accountID to fetch the account-wide label list (the add-label picker).
  accountID?: string;
  clientAccountsEnabled: boolean;
};

// The labels attached to a client account / contact, plus the add/delete
// actions. Ported from the portal's useLabels; the host's clientAccountsEnabled
// arrives as an option instead of the ClientData provider.
export default function useLabels({ accountID, clientAccountsEnabled }: UseLabelsOptions) {
  const api = useApi();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Contacts' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const [labels, setLabels] = useState<string[]>([]);
  const [fetchingLabels, setFetchingLabels] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [labelsLoaded, setLabelsLoaded] = useState(false);

  useEffect(() => {
    fetchLabels()
      .catch((error) => {
        // Portal parity: surface the failure in a dialog and still render the
        // section ("No Labels") rather than hiding it.
        alertDialog.error(
          t('labelsFetchError', { defaultValue: 'Failed to fetch client account labels' }),
          error instanceof Error ? error.message : undefined,
          { abortLabel: tButtons('close', { defaultValue: 'Close' }) },
        );
      })
      .finally(() => setLabelsLoaded(true));
  }, []);

  async function fetchLabels() {
    try {
      setFetchingLabels(true);

      const res = clientAccountsEnabled
        ? await api.fetchClientAccountLabels({ ClientAccountID: accountID })
        : await api.fetchContactLabels({ ContactID: accountID });

      if (!res.Success) throw new Error(res.ErrorMessage);

      setLabels(res.Labels.map((label) => label.Label));

      return res.Labels;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetchingLabels(false);
    }
  }

  async function addLabel(label: string, _accountID = accountID) {
    if (!_accountID) throw new Error('Account ID is required');

    try {
      setSubmitting(true);

      const res = clientAccountsEnabled
        ? await api.addClientAccountLabel({ ClientAccountID: _accountID, Label: label })
        : await api.addContactLabel({ ContactID: _accountID, Label: label });

      if (!res.Success) throw new Error(res.ErrorMessage);

      await fetchLabels();

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteLabel(label: string, _accountID = accountID) {
    if (!_accountID) throw new Error('Account ID is required');

    try {
      setSubmitting(true);

      const res = clientAccountsEnabled
        ? await api.deleteClientAccountLabel({ ClientAccountID: _accountID, Label: label })
        : await api.deleteContactLabel({ ContactID: _accountID, Label: label });

      if (!res.Success) throw new Error(res.ErrorMessage);

      await fetchLabels();

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    labelsLoaded,
    fetchingLabels,
    submitting,
    labels,
    fetchLabels,
    addLabel,
    deleteLabel,
  };
}
